import React, { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { PurchaseDocDetail } from "types/InvoiceInbox";
import { patchPurchaseDoc } from "services/invoice-inbox.service";
import { formatPrettyDate } from "utils/helperFunctions";
import { invoiceNumberWithSeries } from "utils/invoiceInboxStatus";
import { asDateInput } from "./invoice-doc-form";
import { FieldSuggestion, SuggestionAccess, reviewMarkClass } from "./invoice-doc-suggestion";
import { Card, RunChange, buttonClass, inputClass, primaryButtonClass } from "./invoice-doc-ui";

type Props = {
  companyId: string;
  doc: PurchaseDocDetail["doc"];
  busy: boolean;
  /** False for a removed document: nothing here may be written. */
  canEdit: boolean;
  run: RunChange;
  suggestions: SuggestionAccess;
};

type EditableColumn = "invoiceNumber" | "issueDate" | "dueDate";

type EditableRow = {
  column: EditableColumn;
  label: string;
  /** What the row reads when not being edited. */
  shown: string;
  /** What the input starts from (dates as YYYY-MM-DD). */
  inputValue: string;
  type: "text" | "date";
};

/**
 * The invoice's number and dates, each editable in place: click the value,
 * type, Enter (or Save) sends that ONE field through the ordinary PATCH, Esc
 * puts it back. The full form (header menu → Edit) covers everything else.
 */
export default function InvoiceDocDetailsCard({ companyId, doc, busy, canEdit, run, suggestions }: Props) {
  const { t } = useTranslation();
  const [editingColumn, setEditingColumn] = useState<EditableColumn | null>(null);

  const save = (column: EditableColumn, typed: string) => {
    const value = typed.trim();
    return run(async () => {
      // Emptied = cleared, the same as the full form sends it.
      await patchPurchaseDoc(companyId, doc.id, { [column]: value === "" ? null : value });
      setEditingColumn(null);
    });
  };

  const prettyDate = (value: string | null) => (value ? formatPrettyDate(value) : "—");

  // One row per editable field, top to bottom. The series is printed before
  // the number but corrected in the full form — the number is what gets mistyped.
  const rows: EditableRow[] = [
    {
      column: "invoiceNumber",
      label: t("invoice_inbox_col_invoice_number", { defaultValue: "Invoice no." }),
      shown: invoiceNumberWithSeries(doc) || "—",
      inputValue: doc.invoiceNumber || "",
      type: "text",
    },
    {
      column: "issueDate",
      label: t("date", { defaultValue: "Date" }),
      shown: prettyDate(doc.issueDate),
      inputValue: asDateInput(doc.issueDate),
      type: "date",
    },
    {
      column: "dueDate",
      label: t("due_date", { defaultValue: "Due" }),
      shown: prettyDate(doc.dueDate),
      inputValue: asDateInput(doc.dueDate),
      type: "date",
    },
  ];

  return (
    <Card title={t("invoice_inbox_document_card", { defaultValue: "Document" })}>
      {rows.map((row) => (
        <div key={row.column}>
          <InlineEditRow
            label={row.label}
            shown={row.shown}
            inputValue={row.inputValue}
            type={row.type}
            markClass={reviewMarkClass(doc, row.column)}
            editing={editingColumn === row.column}
            canEdit={canEdit}
            busy={busy}
            onStart={() => setEditingColumn(row.column)}
            onCancel={() => setEditingColumn(null)}
            onSave={(typed) => save(row.column, typed)}
          />
          {row.column === "invoiceNumber" && (
            <FieldSuggestion doc={doc} column="invoiceSeries" value={doc.invoiceSeries} access={suggestions} />
          )}
          <FieldSuggestion doc={doc} column={row.column} value={doc[row.column]} access={suggestions} />
        </div>
      ))}
      {/* The approvals describe the document as it was; the full form warns
          the same way. */}
      {editingColumn && doc.approvalSnapshotHash && (
        <p className="mt-1 text-xs text-amber-700">
          {t("invoice_inbox_edit_invalidates", {
            defaultValue:
              "This document has already been approved by someone. Changing an amount, the account or the number withdraws those approvals — they described the document as it was.",
          })}
        </p>
      )}
    </Card>
  );
}

type InlineEditRowProps = {
  label: string;
  shown: string;
  inputValue: string;
  type: "text" | "date";
  markClass: string;
  editing: boolean;
  canEdit: boolean;
  busy: boolean;
  onStart: () => void;
  onCancel: () => void;
  onSave: (typed: string) => void;
};

function InlineEditRow(props: InlineEditRowProps) {
  const { t } = useTranslation();
  const editLabel = `${t("edit", { defaultValue: "Edit" })}: ${props.label}`;
  if (props.editing) return <InlineEditForm {...props} editLabel={editLabel} />;

  return (
    <div className={clsx("flex items-center justify-between gap-3 py-0.5 text-sm", props.markClass)}>
      <span className="text-gray-500">{props.label}</span>
      {props.canEdit ? (
        <button
          type="button"
          aria-label={editLabel}
          className="border-b border-dashed border-gray-400 tabular-nums text-gray-900 hover:border-indigo-600 hover:bg-indigo-50"
          onClick={props.onStart}
        >
          {props.shown}
        </button>
      ) : (
        <span className="tabular-nums text-gray-900">{props.shown}</span>
      )}
    </div>
  );
}

// Mounted only while editing, so every edit starts from the stored value.
// On a phone the form is a two-column grid — label and field each take a full
// row, Save and Cancel share the next one — because one row is about 260px.
function InlineEditForm(props: InlineEditRowProps & { editLabel: string }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(props.inputValue);
  const inputId = useId();
  const unchanged = draft.trim() === props.inputValue;

  return (
    <form
      aria-label={props.editLabel}
      className="flex items-center gap-2 py-1 text-sm max-md:grid max-md:grid-cols-2"
      onSubmit={(event) => {
        event.preventDefault();
        if (!unchanged) props.onSave(draft);
      }}
    >
      <label className="shrink-0 text-gray-500 max-md:col-span-2" htmlFor={inputId}>
        {props.label}
      </label>
      <input
        id={inputId}
        autoFocus
        type={props.type}
        className={clsx(inputClass, "max-md:col-span-2")}
        value={draft}
        disabled={props.busy}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape") props.onCancel();
        }}
      />
      <button type="submit" className={primaryButtonClass} disabled={props.busy || unchanged}>
        {t("save", { defaultValue: "Save" })}
      </button>
      <button type="button" className={buttonClass} disabled={props.busy} onClick={props.onCancel}>
        {t("cancel", { defaultValue: "Cancel" })}
      </button>
    </form>
  );
}
